import React, { useState } from 'react';
import { ArrowLeft, Trophy, Target, Clock, Users, ChevronRight, Play, CheckCircle } from 'lucide-react';
import { MatchplayMatch, MatchplayTournament, MatchplayPlayer } from '../../../types/matchplay-v2';
import { MatchplayScoring } from '../../../lib/matchplay-v2/scoring';
import { formatMatchDate } from '../../../lib/matchplay-v2/utils';
import { useMatchplayTournament } from '../../../hooks/matchplay-v2/useMatchplayTournament';

interface MatchplayLeaderboardProps {
  tournamentId: string;
  onMatchSelect: (matchId: string) => void;
  onBack: () => void;
}

export function MatchplayLeaderboard({ tournamentId, onMatchSelect, onBack }: MatchplayLeaderboardProps) {
  const {
    tournament,
    players,
    matches,
    isLoading,
    error,
    activeMatches,
    completedMatches,
    tournamentProgress
  } = useMatchplayTournament(tournamentId);

  const [selectedFilter, setSelectedFilter] = useState<'all' | 'active' | 'completed'>('all');

  const getMatchTitle = (match: MatchplayMatch, players: MatchplayPlayer[]) => {
    // Safety check for undefined player IDs
    const team1PlayerIds = match.team1Players || [];
    const team2PlayerIds = match.team2Players || [];
    
    const team1PlayersData = players.filter(p => team1PlayerIds.includes(p.id));
    const team2PlayersData = players.filter(p => team2PlayerIds.includes(p.id));

    if (tournament?.format === 'singles') {
      return `${team1PlayersData[0]?.playerName} vs ${team2PlayersData[0]?.playerName}`;
    } else {
      const team1Names = team1PlayersData.map(p => p.playerName).join(' / ');
      const team2Names = team2PlayersData.map(p => p.playerName).join(' / ');
      return `${team1Names} vs ${team2Names}`;
    }
  };

  const getMatchResult = (match: MatchplayMatch) => {
    if (!Array.isArray(match.holes)) return 'Not Started';
    
    const statusCalc = MatchplayScoring.calculateMatchStatus(match);
    const holesPlayed = match.holes.length;
    
    if (statusCalc.status === 'completed') {
      return statusCalc.display || 'Completed';
    } else if (holesPlayed === 0) {
      return 'Not Started';
    } else {
      return statusCalc.display || `AS thru ${holesPlayed}`;
    }
  };

  const getMatchStatusColor = (match: MatchplayMatch) => {
    if (!Array.isArray(match.holes)) return 'bg-blue-100 text-blue-800';
    
    const statusCalc = MatchplayScoring.calculateMatchStatus(match);
    
    if (statusCalc.status === 'completed') {
      return 'bg-green-100 text-green-800';
    } else if (statusCalc.status === 'dormie') {
      return 'bg-amber-100 text-amber-800';
    } else {
      return 'bg-blue-100 text-blue-800';
    }
  };

  const getFilteredMatches = () => {
    if (selectedFilter === 'all') {
      return matches;
    } else if (selectedFilter === 'active') {
      return activeMatches;
    } else if (selectedFilter === 'completed') {
      return completedMatches;
    }
    return [];
  };

  const getTeamStats = () => {
    if (!tournament || tournament.format === 'singles') {
      return null;
    }

    const teamA = players.filter(p => p.teamAssignment === 'A');
    const teamB = players.filter(p => p.teamAssignment === 'B');
    
    const teamAWins = completedMatches.filter(m => m.winner === 'team1').length;
    const teamBWins = completedMatches.filter(m => m.winner === 'team2').length;
    const ties = completedMatches.filter(m => m.winner === 'tie').length;

    // Calculate team points: 1 point for wins, 0.5 points for ties
    const teamAPoints = teamAWins + (ties * 0.5);
    const teamBPoints = teamBWins + (ties * 0.5);

    return {
      teamA: { players: teamA, wins: teamAWins, points: teamAPoints },
      teamB: { players: teamB, wins: teamBWins, points: teamBPoints },
      ties
    };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Loading tournament...</span>
        </div>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">
          {error || 'Tournament not found'}
        </div>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          Back to Tournaments
        </button>
      </div>
    );
  }

  const teamStats = getTeamStats();
  const filteredMatches = getFilteredMatches();

  // Debug logging for status consistency
  if (matches.length > 0) {
    console.log('MatchplayLeaderboard status check:', {
      totalMatches: matches.length,
      activeMatches: activeMatches.length,
      completedMatches: completedMatches.length,
      detailedStatus: matches.map(match => {
        const statusCalc = MatchplayScoring.calculateMatchStatus(match);
        return {
          matchNumber: match.matchNumber,
          holesPlayed: match.holes?.length || 0,
          calculatedStatus: statusCalc.status,
          displayStatus: statusCalc.display,
          originalStatus: match.status
        };
      })
    });
  }

  return (
    <div className="space-y-6">


      {/* Team Overview (for team formats) */}
      {teamStats && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Team Standings</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Team A */}
            <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full"></div>
                <h3 className="font-semibold text-blue-900">Team A</h3>
              </div>
              <div className="space-y-2 mb-4">
                {teamStats.teamA.players.map(player => (
                  <div key={player.id} className="text-sm text-blue-800">
                    {player.playerName} (HC: {player.handicap})
                  </div>
                ))}
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{teamStats.teamA.wins}</div>
                <div className="text-sm text-blue-700">Matches Won</div>
              </div>
            </div>

            {/* Team B */}
            <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 bg-red-500 rounded-full"></div>
                <h3 className="font-semibold text-red-900">Team B</h3>
              </div>
              <div className="space-y-2 mb-4">
                {teamStats.teamB.players.map(player => (
                  <div key={player.id} className="text-sm text-red-800">
                    {player.playerName} (HC: {player.handicap})
                  </div>
                ))}
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{teamStats.teamB.wins}</div>
                <div className="text-sm text-red-700">Matches Won</div>
              </div>
            </div>

            {/* Overall */}
            <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-6 h-6 text-gray-600" />
                <h3 className="font-semibold text-gray-900">Overall</h3>
              </div>
              <div className="space-y-3 text-center">
                <div>
                  <div className="text-lg font-bold text-blue-600">{teamStats.teamA.points}</div>
                  <div className="text-sm text-gray-500">Team A Points</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-red-600">{teamStats.teamB.points}</div>
                  <div className="text-sm text-gray-500">Team B Points</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-gray-600">{activeMatches.length}</div>
                  <div className="text-sm text-gray-500">Active Matches</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Matches Section */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Matches</h2>
          
          {/* Filter Tabs - Mobile Friendly */}
          <div className="flex bg-gray-100 rounded-lg p-1 max-w-full overflow-x-auto">
            {(['all', 'active', 'completed'] as const).map((filterOption) => (
              <button
                key={filterOption}
                onClick={() => setSelectedFilter(filterOption)}
                className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors font-medium whitespace-nowrap ${
                  selectedFilter === filterOption
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
                <span className="ml-1 text-xs">
                  ({filterOption === 'all' ? (matches?.length || 0) : 
                    filterOption === 'active' ? (activeMatches?.length || 0) : 
                    (completedMatches?.length || 0)})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Matches List */}
        <div className="space-y-4">
          {filteredMatches.map((match) => (
            <div
              key={match.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">
                      {getMatchTitle(match, players)}
                    </h3>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${getMatchStatusColor(match)}`}>
                      {getMatchResult(match)}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Target className="w-3 h-3" />
                      {Array.isArray(match.holes) ? match.holes.length : 0} holes played
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatMatchDate(match.createdAt)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => onMatchSelect(match.id)}
                  className="ml-4 flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  {(() => {
                    if (!Array.isArray(match.holes)) {
                      return (
                        <>
                          <Play className="w-4 h-4" />
                          Start Match
                        </>
                      );
                    }
                    
                    const statusCalc = MatchplayScoring.calculateMatchStatus(match);
                    if (statusCalc.status === 'completed') {
                      return (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          View Results
                        </>
                      );
                    } else {
                      return (
                        <>
                          <Play className="w-4 h-4" />
                          {match.holes.length === 0 ? 'Start Match' : 'Continue'}
                        </>
                      );
                    }
                  })()}
                </button>
              </div>
            </div>
          ))}

          {filteredMatches.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Target className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>No {selectedFilter === 'all' ? '' : selectedFilter} matches found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 